import {
  convertLabelMapToConfig,
  convertLabelUomMapToConfig,
  mergeConfig,
} from 'Components/Worksheets/Core/Helpers/configHelpers';

const getLabelConfig = (prefix: string) =>
  mergeConfig(
    convertLabelMapToConfig({
      [`${prefix}.Preparation.AreSieveMassesCumulative`]: 'Sieve Retained Masses Are',
      [`${prefix}.Preparation.PercentageType`]: 'Calculate and Apply Limits to',
      [`${prefix}.Preparation.AddSieveSizesBasedOnSpecification`]: 'Sieves Are Added',

      [`${prefix}.Preparation.VisualNominalParticleSize`]: 'Nominal Particle Size',
      [`${prefix}.Preparation.SampleContainsFineMaterial`]: 'Sample contains fine material',
      [`${prefix}.Preparation.DryingPhase`]: 'Material Dried',
      [`${prefix}.Preparation.WashingPhase`]: 'Material Washed',
      [`${prefix}.Preparation.TotalDryMassDeterminationBy`]: 'Total Dry Mass Determination By',

      [`${prefix}.WholePortion.Masses.MassMeasurement.Equipment.Balance`]: 'Sampling Balance',
      [`${prefix}.WholePortion.Masses.MassMeasurement.Equipment.DryMassBalance`]: 'Sampling Balance',
      [`${prefix}.WholePortion.Masses.DryMass`]: 'Portion Dry Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.DryMass`]: 'Portion Dry Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.TareID`]: 'Tare ID',
      [`${prefix}.WholePortion.Masses.MassMeasurement.TareMass`]: 'Tare Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.TareAndDryMass`]: 'Tare and Portion Dry Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.TareAndWetMass`]: 'Tare and Portion Wet Mass',
      [`${prefix}.WholePortion.Masses.MassMeasurement.WetMass`]: 'Portion Wet Mass',
      [`${prefix}.WholePortion.Masses.MoistureContent`]: 'Moisture Content',
      [`${prefix}.WholePortion.Masses.AssociatedMoistureContent.ExternalTest.Results.MoistureContentPercent`]:
        'Moisture Content',
      [`${prefix}.WholePortion.Masses.WetMass`]: 'Specimen Total Mass',
      [`${prefix}.WholePortion.Washing.WashedDryMass`]: 'Specimen Total Washed Dry Mass',
      [`${prefix}.WholePortion.Sieving.Equipment.Balance`]: 'Balance',
      [`${prefix}.WholePortion.Sieving.Equipment.SieveSet`]: 'Sieve Set',
      [`${prefix}.Results.ChartOptions.CurveFittingAlgorithm`]: 'Curve Fitting',
      [`${prefix}.Results.ChartOptions.JoinSpecificationPoints`]: 'Join specification points on chart',

      [`${prefix}.Results.CoefficientOfUniformity`]: 'Coefficient of Uniformity (Cu)',
      [`${prefix}.Results.CoefficientOfCurvature`]: 'Coefficient of Curvature (Cc)',
      [`${prefix}.Results.FinenessModulus`]: 'Fineness Modulus (Fm)',
      [`${prefix}.Results.ReportCoefficientOfUniformity`]: 'Coefficient of Uniformity',
      [`${prefix}.Results.ReportCoefficientOfCurvature`]: 'Coefficient of Curvature',
      [`${prefix}.Results.ReportFinenessModulus`]: 'Fineness Modulus',
    }),
    convertLabelUomMapToConfig({
      [`${prefix}.WholePortion.Masses.MoistureContent`]: '%',
      [`${prefix}.WholePortion.Masses.AssociatedMoistureContent.ExternalTest.Results.MoistureContentPercent`]: '%',
    }),
    getConstantDryMassLabelConfig(`${prefix}.WholePortion.Masses.MassMeasurement.MeasuredConstantDryMass`, 'Material'),
    getConstantDryMassLabelConfig(
      `${prefix}.WholePortion.Separation.FinePortion.Subspecimen.Masses.MassMeasurement.MeasuredConstantDryMass`,
      'Subspecimen'
    ),
    getConstantDryMassLabelConfig(
      `${prefix}.WholePortion.Separation.FinePortion.Subspecimen.Separation.FinePortion.Subspecimen.Masses.MassMeasurement.MeasuredConstantDryMass`,
      'Subspecimen'
    ),
    getSeparationLabelConfig(`${prefix}.WholePortion.Separation`),
    getSeparationLabelConfig(`${prefix}.WholePortion.Separation.FinePortion.Subspecimen.Separation`),
    getSeparationLabelConfig(
      `${prefix}.WholePortion.Separation.FinePortion.Subspecimen.Separation.FinePortion.Subspecimen.Separation`
    )
  );

const getConstantDryMassLabelConfig = (prefix: string, captionPrefix: string) =>
  mergeConfig(
    convertLabelMapToConfig({
      [`${prefix}.Equipment.DryingEquipment`]: 'Drying by',
      [`${prefix}.Equipment.Oven`]: 'Oven',
      [`${prefix}.Equipment.MicrowaveOven`]: 'Microwave Oven',
      [`${prefix}.Equipment.InfraredLight`]: 'Infrared Light',
      [`${prefix}.Equipment.Hotplate`]: 'Hotplate',
      [`${prefix}.Equipment.DryingTemperature`]: 'Oven Temperature',
      [`${prefix}.DryingTray.DateTimePlacedInOvenLocal`]: `Date and Time in Oven`,
      [`${prefix}.Equipment.DryMassBalance`]: 'Dry Mass Balance',
      [`${prefix}.Measurements.TotalDryMass`]: `${captionPrefix} Dry Mass`,
      [`${prefix}.Measurements.TotalDryMassToTotalWetMassRatio`]: `${captionPrefix} Dry to Wet Mass Ratio`,
      [`${prefix}.DryingTray.DryMassReadings.DryingTime`]: `Drying Time`,
    })
  );
/**
 * @param prefix prefix for the Separation (e.g. ...WholePortion.Separation)
 */
const getSeparationLabelConfig = (prefix: string) =>
  mergeConfig(
    convertLabelMapToConfig({
      [`${prefix}.SeparatingSieveSizeId`]: 'Separating Sieve Size',
    }),
    getPortionLabelConfig(`${prefix}.CoarsePortion`, 'Coarse Portion'),
    getPortionLabelConfig(`${prefix}.FinePortion`, 'Fine Portion'),
    getPortionLabelConfig(`${prefix}.FinePortion.Subspecimen`, 'Subspecimen')
  );

/**
 * @param prefix prefix for the Separation (e.g. ...WholePortion.Separation.CoarsePortion)
 * @param captionPrefix prefix for mass captions (e.g. 'Coarse Portion')
 */
const getPortionLabelConfig = (prefix: string, captionPrefix: string) =>
  convertLabelMapToConfig({
    [`${prefix}.Separation.SeparatingSieveSizeId`]: 'Separating Sieve Size',
    [`${prefix}.Masses.MassMeasurement.Equipment.Balance`]: 'Balance',
    [`${prefix}.Masses.MassMeasurement.Equipment.DryMassBalance`]: 'Dry Mass Balance',
    [`${prefix}.Masses.MassMeasurement.Equipment.Oven`]: 'Oven',
    [`${prefix}.Masses.MassMeasurement.TareID`]: `Tare ID`,

    [`${prefix}.Masses.MassMeasurement.TareAndWetMass`]: `Tare and ${captionPrefix} Wet Mass`,
    [`${prefix}.Masses.MassMeasurement.TareAndDryMass`]: `Tare and ${captionPrefix} Dry Mass`,
    [`${prefix}.Masses.MassMeasurement.TareMass`]: `Tare Mass`,
    [`${prefix}.Masses.MassMeasurement.WetMass`]: `${captionPrefix} Wet Mass`,
    [`${prefix}.Masses.WetMass`]: `${captionPrefix} Mass`,
    [`${prefix}.Masses.DryMass`]: `${captionPrefix} Dry Mass`,
    [`${prefix}.Sieving.Equipment.Balance`]: 'Balance',
    [`${prefix}.Sieving.Equipment.SieveSet`]: 'Sieve Set',
    [`${prefix}.Washing.WashedDryMass`]: 'Specimen Total Washed Dry Mass',
  });

export default getLabelConfig;
